import axios from 'axios';

/**
 * FE-17: data fetching lives in a shared hook and components never call Axios
 * directly. This module is the only place an HTTP client is configured.
 *
 * FE-18 / MA-5: the access token is held in memory — never localStorage, which
 * any script running on the page can read. The refresh token is an HTTP-only
 * cookie the browser sends on its own; JavaScript cannot see it at all.
 */
let accessToken = null;
let onSessionLost = null;

export const setAccessToken = (token) => { accessToken = token; };
export const getAccessToken = () => accessToken;
export const onSessionExpired = (handler) => { onSessionLost = handler; };

/**
 * Where the API lives.
 *
 * `VITE_API_URL` used to be spliced in raw, which meant it had to be the full
 * base *including* `/api/v1` — and setting it to a bare origin silently stripped
 * the prefix from every request, so the whole app 404'd with "No route matches
 * POST /auth/login". A configuration value with one correct spelling and no
 * error for the wrong one is a trap, so this accepts either.
 *
 * Unset is the normal case and the best one: a relative `/api/v1` is same-origin,
 * which means no CORS preflight on every request and no third-party-cookie
 * question for the refresh token. Vite proxies it in development and Nginx does
 * in production.
 */
const API_PREFIX = '/api/v1';

function apiBaseUrl() {
  const configured = String(import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
  if (!configured) return API_PREFIX;

  // Already points at the versioned API — use it as given.
  if (configured.endsWith(API_PREFIX)) return configured;

  return `${configured}${API_PREFIX}`;
}

export const api = axios.create({
  baseURL: apiBaseUrl(),
  withCredentials: true,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * FE-12: the message the API returned, including field-level validation detail.
 * A generic "something went wrong" when the server said something specific is
 * the single most common way a good API is made to feel broken.
 */
export class ApiError extends Error {
  constructor({ code, message, details, status, requestId }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.status = status;
    this.requestId = requestId;
  }

  /** Field name -> message, for inline errors beside the input that caused them. */
  get fieldErrors() {
    const issues = this.details?.issues ?? [];
    return issues.reduce((acc, issue) => {
      const key = String(issue.field ?? '').split('.').pop();
      if (key && !acc[key]) acc[key] = issue.message;
      return acc;
    }, {});
  }
}

function normalise(error) {
  if (error.response?.data?.error) {
    const { code, message, details } = error.response.data.error;
    return new ApiError({
      code, message, details,
      status: error.response.status,
      requestId: error.response.data.requestId,
    });
  }
  if (error.code === 'ECONNABORTED') {
    return new ApiError({ code: 'timeout', message: 'The request timed out. Please try again.', status: 0 });
  }
  return new ApiError({
    code: 'network_error',
    message: 'Could not reach the server. Check your connection and try again.',
    status: error.response?.status ?? 0,
  });
}

/**
 * FE-18: "concurrent 401s share a single refresh." Without this, a dashboard
 * screen firing four requests at once on an expired token performs four
 * refreshes, three of which race — and because each refresh rotates the token,
 * the losers end up holding one that is already stale.
 */
let refreshPromise = null;

async function refreshSession() {
  refreshPromise ??= api
    .post('/auth/refresh')
    .then((response) => {
      setAccessToken(response.data.data.accessToken);
      return response.data.data;
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config ?? {};
    const isAuthCall = String(original.url ?? '').includes('/auth/');

    if (error.response?.status === 401 && !original.__retried && !isAuthCall) {
      original.__retried = true;
      try {
        await refreshSession();
        return await api(original);
      } catch {
        setAccessToken(null);
        onSessionLost?.();
        throw normalise(error);
      }
    }

    throw normalise(error);
  },
);

/** Unwraps the { success, data } envelope so callers work with the payload. */
const unwrap = (response) => response.data?.data ?? response.data;

export const http = {
  get: (url, config) => api.get(url, config).then(unwrap),
  post: (url, body, config) => api.post(url, body, config).then(unwrap),
  put: (url, body, config) => api.put(url, body, config).then(unwrap),
  patch: (url, body, config) => api.patch(url, body, config).then(unwrap),
  delete: (url, config) => api.delete(url, config).then(unwrap),
  /** Kept separate: a replay needs the envelope's idempotentReplay flag. */
  raw: (config) => api(config).then((r) => r.data),
};

export { refreshSession };
